import { useI18n } from "vue-i18n";
import { useDisplayLocale } from "@/shared/composables/useDisplayLocale";
export function useFinanceFormatters() {
    const { locale } = useI18n();
    const displayLocale = useDisplayLocale();
    /**
     * "–" for a missing amount or currency: a record still loading has neither, and formatting it
     * in a guessed currency would show a symbol the record does not carry.
     */
    function formatAmount(amount, currency) {
        if (amount == null || !currency)
            return "–";
        return new Intl.NumberFormat(displayLocale.value, {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
        }).format(amount);
    }
    /**
     * Currency without decimals, for hero/KPI figures ("150 €", "148 750 €") where the cents add
     * noise rather than precision. Data cells and anything reconciled to the cent keep
     * {@link formatAmount}.
     */
    function formatAmountCompact(amount, currency) {
        if (amount == null || !currency)
            return "–";
        return new Intl.NumberFormat(displayLocale.value, {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    }
    /**
     * Parses a user-typed amount string using the active locale's decimal and
     * group separators. `parseFloat` is locale-blind — in fr-FR / nl-NL a user
     * typing "1,50" gets 1 (the decimals are silently truncated at the comma).
     * Returns NaN for unparseable input so callers can fall back.
     */
    function parseAmount(value) {
        if (value == null)
            return NaN;
        const trimmed = value.trim();
        if (trimmed === "")
            return NaN;
        // Derive the locale's separators from a sample formatted number.
        const parts = new Intl.NumberFormat(displayLocale.value).formatToParts(12345.6);
        const group = parts.find((p) => p.type === "group")?.value ?? ",";
        const decimal = parts.find((p) => p.type === "decimal")?.value ?? ".";
        const normalized = trimmed
            .split(group)
            .join("") // strip group separators
            .split(decimal)
            .join(".") // normalize decimal separator to "."
            .replace(/\s/g, ""); // strip any remaining spaces (e.g. fr narrow no-break)
        return parseFloat(normalized);
    }
    /**
     * The inverse of {@link parseAmount}: an ungrouped amount string to seed an EDITABLE input.
     * `String(amount)` is not an inverse — it always emits a "." decimal, which parseAmount then
     * strips as the group separator under nl-NL, so 1000.5 round-trips to 10005 with no error
     * anywhere. Display-only figures keep {@link formatAmount}.
     */
    function formatAmountForInput(amount) {
        if (amount == null)
            return "";
        return amount.toLocaleString(displayLocale.value, { useGrouping: false, maximumFractionDigits: 2 });
    }
    /**
     * A rate already expressed in percent points (46.4 -> "46,4 %"), in the active locale. The page's
     * percent columns were each formatting their own way - one through Intl's percent style, the others
     * by hand - so two adjacent widgets rendered the same quantity with different spacing in fr. This is
     * the hand-written shape, which is the one already on screen.
     */
    function formatPercent(value, fractionDigits = 1) {
        if (value == null)
            return "–";
        return `${value.toLocaleString(locale.value, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        })} %`;
    }
    function formatDate(date) {
        if (!date)
            return "–";
        return new Intl.DateTimeFormat(locale.value, { dateStyle: "medium" }).format(new Date(date));
    }
    /** Compact date for timeline badges (e.g. 21/11/2025) */
    function formatBadgeDate(date) {
        return new Intl.DateTimeFormat(locale.value, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }).format(new Date(date));
    }
    return {
        formatAmount,
        formatAmountCompact,
        formatAmountForInput,
        formatPercent,
        parseAmount,
        formatDate,
        formatBadgeDate,
    };
}
